import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  BookOpen, Sparkles, BookText, FilePlus, Gamepad2, Highlighter, Presentation,
  Layers, Calendar, Link as LinkIcon,
} from 'lucide-react';
import { COMPANION_ACTION_TYPES } from './companionActionTypes';
import { LumeraPlusBadge } from './PremiumLock';

const PREMIUM_ACTIONS = new Set(['word_study']);

const ICONS = {
  open_verse: BookOpen,
  word_study: Sparkles,
  mh_commentary: BookText,
  sermon_prep: FilePlus,
  play_game: Gamepad2,
  view_highlights: Highlighter,
  preach_mode: Presentation,
  verse_lens: Layers,
  view_chronology: Calendar,
  cross_refs: LinkIcon,
};

// Sanity check — fails loudly during dev if the icon map drifts from
// the single source of truth in companionActionTypes.js.
if (process.env.NODE_ENV !== 'production') {
  const missing = COMPANION_ACTION_TYPES.filter((t) => !(t in ICONS));
  if (missing.length) {
    // eslint-disable-next-line no-console
    console.warn('CompanionActions: missing icons for', missing);
  }
}

/**
 * Build a deep-link URL inside Lumera for each action type emitted by the
 * resourceful companion. Keeps the navigation centralised so the JSON
 * contract from the LLM stays loose ("type + params") and the frontend
 * decides exactly where each lands.
 */
const urlFor = (action) => {
  const p = action?.params || {};
  switch (action.type) {
    case 'open_verse': {
      const sp = new URLSearchParams();
      if (p.book) sp.set('book', p.book);
      if (p.chapter != null) sp.set('chapter', String(p.chapter));
      if (p.verse != null) sp.set('verse', String(p.verse));
      const qs = sp.toString();
      return `/bible${qs ? `?${qs}` : ''}`;
    }
    case 'word_study': {
      const sp = new URLSearchParams();
      if (p.word) sp.set('word', p.word);
      if (p.strongs) sp.set('strongs', p.strongs);
      const qs = sp.toString();
      return `/word-study${qs ? `?${qs}` : ''}`;
    }
    case 'mh_commentary': {
      // Navigate to the Bible reader at the requested book+chapter — the
      // MH sheet button is pinned right there so the user is one tap away.
      const sp = new URLSearchParams();
      if (p.book) sp.set('book', p.book);
      if (p.chapter != null) sp.set('chapter', String(p.chapter));
      sp.set('mh', '1');
      return `/bible?${sp.toString()}`;
    }
    case 'sermon_prep': {
      const sp = new URLSearchParams();
      if (p.topic) sp.set('topic', p.topic);
      const qs = sp.toString();
      return `/sermon-prep${qs ? `?${qs}` : ''}`;
    }
    case 'play_game': {
      const sp = new URLSearchParams({ tab: 'games' });
      if (p.game) sp.set('game', p.game);
      return `/community?${sp.toString()}`;
    }
    case 'view_highlights':
      return '/highlights';
    case 'preach_mode': {
      const sp = new URLSearchParams();
      if (p.draft_id) sp.set('draft_id', p.draft_id);
      const qs = sp.toString();
      return `/sermon-prep/present${qs ? `?${qs}` : ''}`;
    }
    case 'view_chronology': {
      const sp = new URLSearchParams();
      if (p.section) sp.set('section', p.section);
      const qs = sp.toString();
      return `/chronology${qs ? `?${qs}` : ''}`;
    }
    case 'cross_refs': {
      const sp = new URLSearchParams();
      if (p.book) sp.set('book', p.book);
      if (p.chapter != null) sp.set('chapter', String(p.chapter));
      if (p.verse != null) sp.set('verse', String(p.verse));
      const qs = sp.toString();
      return `/cross-references${qs ? `?${qs}` : ''}`;
    }
    default:
      return '/';
  }
};

/**
 * Renders the LLM's `actions` array as a row of clickable Lumera-tool
 * deep-link buttons. Each button navigates inside the SPA so the
 * companion can guide the user to the next step instead of just answering.
 */
const CompanionActions = ({ actions, language = 'en', isPremium = true, onRequirePremium = () => true }) => {
  const navigate = useNavigate();
  if (!Array.isArray(actions) || actions.length === 0) return null;

  const handleClick = (a) => {
    if (!isPremium && PREMIUM_ACTIONS.has(a.type)) {
      onRequirePremium(language === 'es' ? "Estudio de palabras de Strong" : "Strong's word study");
      return;
    }
    // Verse Lens opens via a custom event (the global VerseLensHost listens).
    if (a.type === 'verse_lens') {
      const p = a.params || {};
      if (p.book && p.chapter != null && p.verse != null) {
        window.dispatchEvent(new CustomEvent('lumera:verse-lens', {
          detail: { book: p.book, chapter: Number(p.chapter), verse: Number(p.verse) },
        }));
      }
      return;
    }
    navigate(urlFor(a));
  };

  return (
    <div className="space-y-2" data-testid="companion-actions">
      <p className="text-xs font-semibold uppercase tracking-widest text-purple-700 dark:text-purple-300">
        {language === 'es' ? 'Próximos pasos' : 'Take the next step'}
      </p>
      <div className="flex flex-wrap gap-2">
        {actions.map((a, i) => {
          const Icon = ICONS[a.type] || Sparkles;
          return (
            <Button
              key={`${a.type}-${i}`}
              onClick={() => handleClick(a)}
              variant="outline"
              size="sm"
              className="h-8 text-xs border-purple-300 text-purple-800 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-200 dark:hover:bg-purple-900/40"
              data-testid={`companion-action-${a.type}-${i}`}
            >
              <Icon className="w-3.5 h-3.5 mr-1.5" />
              {a.label}
              {!isPremium && PREMIUM_ACTIONS.has(a.type) && <LumeraPlusBadge className="ml-1.5" />}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default CompanionActions;
